import { useState, useEffect, useMemo } from 'react';
import ScrollReveal from '@/components/ScrollReveal';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Star, CheckCircle, CalendarIcon, ArrowRight, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { DateRange } from 'react-day-picker';
import balconyViewSivota from '@/assets/balcony-view-sivota.jpg';
import originalZeusPool from '@/assets/original-zeus-pool.jpg';
import heraFeature from '@/assets/hera-feature.jpg';
import hera1 from '@/assets/hera-1.jpg';
import zeus1 from '@/assets/zeus-1.jpg';
import zeus2 from '@/assets/zeus-2.jpg';
import hera2 from '@/assets/hera-2.jpg';

type Season = {
  id: string; name: string; start_date: string; end_date: string;
  min_stay: number; price_hera: number; price_zeus: number;
};

export default function Booking() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', checkin: '', checkout: '', guests: '', apartment: '', message: '', coupon: '' });
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [step, setStep] = useState(1);
  const { t } = useLanguage();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [pricing, setPricing] = useState<{ nights: number; pricePerNight: number; total: number; minStay: number; seasonName: string; error: string } | null>(null);
  const [couponDiscount, setCouponDiscount] = useState<{ type: 'fixed' | 'percentage'; value: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const disabledDates = useMemo(() => {
    if (seasons.length === 0) return undefined;
    return (date: Date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return !seasons.some(s => dateStr >= s.start_date && dateStr <= s.end_date);
    };
  }, [seasons]);

  useEffect(() => {
    supabase.from('seasons').select('*').eq('is_active', true).then(({ data }) => {
      setSeasons((data as Season[]) ?? []);
    });
  }, []);

  useEffect(() => {
    if (!form.checkin || !form.checkout || !form.apartment) { setPricing(null); return; }
    const checkin = new Date(form.checkin);
    const checkout = new Date(form.checkout);
    const nights = Math.ceil((checkout.getTime() - checkin.getTime()) / (1000 * 60 * 60 * 24));
    if (nights <= 0) { setPricing(null); return; }
    const priceKey = form.apartment === 'hera' ? 'price_hera' : 'price_zeus';
    const season = seasons.find(s => form.checkin >= s.start_date && form.checkin <= s.end_date && Number(s[priceKey]) > 0)
      || seasons.find(s => form.checkin >= s.start_date && form.checkin <= s.end_date);
    const pricePerNight = season ? (form.apartment === 'hera' ? Number(season.price_hera) : Number(season.price_zeus)) : 0;
    const minStay = season?.min_stay ?? 1;
    const seasonName = season?.name ?? '';
    const error = nights < minStay ? t('minimumStay').replace('{min}', String(minStay)) : '';
    setPricing({ nights, pricePerNight, total: nights * pricePerNight, minStay, seasonName, error });
  }, [form.checkin, form.checkout, form.apartment, seasons, t]);

  const applyCoupon = async () => {
    if (!form.coupon.trim()) return;
    const { data } = await supabase.from('coupons').select('*').eq('code', form.coupon.toUpperCase()).eq('is_active', true).maybeSingle();
    if (!data) { toast.error(t('invalidCoupon')); setCouponDiscount(null); return; }
    setCouponDiscount({ type: data.discount_type as 'fixed' | 'percentage', value: Number(data.discount_value) });
    toast.success(`Coupon applied: ${data.discount_type === 'percentage' ? `${data.discount_value}% off` : `€${data.discount_value} off`}`);
  };

  const getFinalTotal = () => {
    if (!pricing) return 0;
    if (!couponDiscount) return pricing.total;
    if (couponDiscount.type === 'percentage') return pricing.total * (1 - couponDiscount.value / 100);
    return Math.max(0, pricing.total - couponDiscount.value);
  };

  const goToStep2 = () => {
    if (!form.apartment) { toast.error(t('selectApartment')); return; }
    if (!form.checkin || !form.checkout) { toast.error(t('selectDates')); return; }
    if (pricing?.error) { toast.error(pricing.error); return; }
    setStep(2);
    const formEl = document.getElementById('booking-form');
    if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t('fillNameEmail'));
      return;
    }
    setSubmitting(true);
    try {
      const discountAmt = pricing ? pricing.total - getFinalTotal() : 0;
      const enquiryData = {
        name: form.name.trim(), email: form.email.trim(),
        phone: form.phone.trim() || null, apartment: form.apartment,
        checkin: form.checkin, checkout: form.checkout,
        guests: Number(form.guests) || 1, message: form.message.trim() || null,
        total_amount: getFinalTotal(), discount_amount: discountAmt,
        coupon_code: form.coupon.toUpperCase() || null,
      };
      const { error } = await supabase.from('enquiries').insert(enquiryData);
      if (error) throw error;
      supabase.functions.invoke('send-enquiry-email', { body: enquiryData }).catch(err => console.error('Email send failed:', err));
      toast.success(t('thankYouEnquiry'));
      setForm({ name: '', email: '', phone: '', checkin: '', checkout: '', guests: '', apartment: '', message: '', coupon: '' });
      setDateRange(undefined); setPricing(null); setCouponDiscount(null); setStep(1);
    } catch (err: any) {
      toast.error(err.message || 'Failed to send enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const propertyImages = [
    { src: heraFeature, alt: 'Hera apartment view' }, { src: originalZeusPool, alt: 'Zeus pool area' },
    { src: hera1, alt: 'Hera interior' }, { src: zeus1, alt: 'Zeus interior' },
    { src: hera2, alt: 'Hera terrace' }, { src: zeus2, alt: 'Zeus lounge' },
  ];

  return (
    <div>
      {/* ═══ HERO ═══ */}
      <section className="relative h-[75vh] flex items-end overflow-hidden">
        <img src={balconyViewSivota} alt="Balcony view over Sivota" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 lg:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('reserve')}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white drop-shadow-lg">{t('bookYourStay')}</h1>
        </div>
      </section>

      {/* ═══ TRUST BAR ═══ */}
      <section className="bg-secondary py-12">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { icon: Star, label: t('ratingLabel'), desc: t('exceptionalBooking') },
              { icon: CheckCircle, label: t('instantConfirmation'), desc: t('bookWithConfidence') },
              { icon: CheckCircle, label: t('freeCancellation'), desc: t('flexibleBooking') },
            ].map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-3" aria-hidden="true">
                    <item.icon size={20} className="text-accent" />
                  </div>
                  <p className="font-serif text-lg mb-1">{item.label}</p>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PROPERTY IMAGES STRIP ═══ */}
      <section className="py-6 bg-background" aria-label="Property gallery">
        <div className="section-padding">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {propertyImages.map((img, i) => (
              <div key={i} className="aspect-square rounded-sm overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FORM SECTION ═══ */}
      <section className="section-padding py-24 lg:py-36" aria-label="Booking form">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <ScrollReveal>
              <div>
                <p className="text-luxury mb-4">{t('option1')}</p>
                <h2 className="font-serif text-3xl font-light mb-2">{t('sendAnEnquiry')}</h2>
                <div className="w-12 h-px bg-golden mb-6" />

                {/* Step indicator */}
                <div className="flex items-center gap-3 mb-8" role="navigation" aria-label="Form steps">
                  <div className={cn("flex items-center gap-2", step === 1 ? "text-foreground" : "text-muted-foreground")}>
                    <span className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border transition-colors", step === 1 ? "bg-accent text-accent-foreground border-accent" : "border-border")} aria-current={step === 1 ? 'step' : undefined}>1</span>
                    <span className="text-xs tracking-wider uppercase hidden sm:inline">{t('stayDetails')}</span>
                  </div>
                  <div className="w-8 h-px bg-border" aria-hidden="true" />
                  <div className={cn("flex items-center gap-2", step === 2 ? "text-foreground" : "text-muted-foreground")}>
                    <span className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border transition-colors", step === 2 ? "bg-accent text-accent-foreground border-accent" : "border-border")} aria-current={step === 2 ? 'step' : undefined}>2</span>
                    <span className="text-xs tracking-wider uppercase hidden sm:inline">{t('yourDetails')}</span>
                  </div>
                </div>

                <form id="booking-form" onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {step === 1 && (
                    <fieldset className="space-y-6 border-0 p-0 m-0">
                      <legend className="sr-only">{t('stayDetails')}</legend>
                      <div>
                        <label id="apartment-label" className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{t('apartments')}</label>
                        <div className="grid grid-cols-2 gap-3 mt-3" role="radiogroup" aria-labelledby="apartment-label">
                          {[
                            { value: 'hera', label: 'Hera', img: heraFeature, desc: t('seaViewsTerrace') },
                            { value: 'zeus', label: 'Zeus', img: originalZeusPool, desc: t('privatePoolCourtyard') },
                          ].map(apt => (
                            <button key={apt.value} type="button" role="radio" aria-checked={form.apartment === apt.value}
                              onClick={() => setForm(p => ({ ...p, apartment: p.apartment === apt.value ? '' : apt.value }))}
                              className={cn("relative rounded-sm overflow-hidden aspect-[4/3] group border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                                form.apartment === apt.value ? 'border-accent ring-2 ring-accent/30' : 'border-border/40 hover:border-accent/50')}>
                              <img src={apt.img} alt={apt.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                              <div className="absolute bottom-0 left-0 right-0 p-3">
                                <p className="font-serif text-xl text-white">{apt.label}</p>
                                <p className="text-white/80 text-xs">{apt.desc}</p>
                              </div>
                              {form.apartment === apt.value && (
                                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center" aria-hidden="true">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label id="dates-label" className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{t('checkin')} — {t('checkout')}</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button aria-labelledby="dates-label" className={cn("w-full flex items-center gap-2 border-b border-border py-3 text-left text-foreground transition-colors hover:border-accent focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring", !dateRange?.from && "text-muted-foreground")}>
                              <CalendarIcon size={16} className="text-muted-foreground" aria-hidden="true" />
                              {dateRange?.from && dateRange?.to ? `${format(dateRange.from, 'dd MMM yyyy')} → ${format(dateRange.to, 'dd MMM yyyy')}` : t('selectYourDates')}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="range" selected={dateRange}
                              onSelect={(range: DateRange | undefined) => {
                                setDateRange(range);
                                setForm(p => ({ ...p, checkin: range?.from ? format(range.from, 'yyyy-MM-dd') : '', checkout: range?.to ? format(range.to, 'yyyy-MM-dd') : '' }));
                              }}
                              disabled={disabledDates} numberOfMonths={2} className="p-3 pointer-events-auto" />
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div>
                        <label htmlFor="guests-input" className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{t('guests')}</label>
                        <input id="guests-input" type="number" min="1" max="10" value={form.guests}
                          onChange={e => setForm(p => ({ ...p, guests: e.target.value }))}
                          className="w-full bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors" />
                      </div>

                      {pricing && (
                        <div className="bg-secondary/50 border border-border rounded-sm p-4 space-y-2" role="status" aria-live="polite">
                          {pricing.seasonName && <p className="text-xs text-accent font-medium tracking-wider uppercase mb-1">{pricing.seasonName}</p>}
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{pricing.nights} nights × €{pricing.pricePerNight}/night</span>
                            <span>€{pricing.total}</span>
                          </div>
                          {couponDiscount && (
                            <div className="flex justify-between text-sm text-green-700">
                              <span>{t('discount')} ({couponDiscount.type === 'percentage' ? `${couponDiscount.value}%` : `€${couponDiscount.value}`})</span>
                              <span>−€{(pricing.total - getFinalTotal()).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-serif text-lg border-t border-border pt-2">
                            <span>{t('total')}</span>
                            <span>€{getFinalTotal().toFixed(2)}</span>
                          </div>
                          {pricing.error && <p className="text-destructive text-xs mt-1" role="alert">{pricing.error}</p>}
                          {pricing.pricePerNight === 0 && <p className="text-muted-foreground text-xs">{t('noPricingSet')}</p>}
                        </div>
                      )}

                      <div>
                        <label htmlFor="coupon-input" className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{t('couponCode')}</label>
                        <div className="flex gap-2">
                          <input id="coupon-input" value={form.coupon} onChange={e => setForm(p => ({ ...p, coupon: e.target.value }))}
                            className="flex-1 bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors uppercase"
                            placeholder={t('enterCode')} maxLength={50} />
                          <button type="button" onClick={applyCoupon}
                            className="px-4 py-2 text-xs tracking-wider uppercase border border-border rounded-sm hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                            {t('apply')}
                          </button>
                        </div>
                      </div>

                      <button type="button" onClick={goToStep2}
                        className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors mt-2 w-full sm:w-auto flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                        {t('continueBtn')} <ArrowRight size={16} aria-hidden="true" />
                      </button>
                    </fieldset>
                  )}

                  {step === 2 && (
                    <fieldset className="space-y-6 border-0 p-0 m-0">
                      <legend className="sr-only">{t('yourDetails')}</legend>
                      <div className="bg-secondary/50 border border-border rounded-sm p-4 space-y-1 text-sm">
                        <p className="text-xs text-accent font-medium tracking-wider uppercase mb-2">{t('yourSelection')}</p>
                        <p className="text-foreground capitalize">{form.apartment} {t('apartment')}</p>
                        {dateRange?.from && dateRange?.to && <p className="text-muted-foreground">{format(dateRange.from, 'dd MMM yyyy')} → {format(dateRange.to, 'dd MMM yyyy')}</p>}
                        {pricing && <p className="font-serif text-lg text-foreground mt-1">€{getFinalTotal().toFixed(2)}</p>}
                        <button type="button" onClick={() => setStep(1)} className="text-accent text-xs underline underline-offset-2 mt-2 hover:text-accent/80 focus:outline-none focus:ring-1 focus:ring-ring rounded-sm">
                          {t('editSelection')}
                        </button>
                      </div>

                      {[
                        { key: 'name', label: t('fullName'), type: 'text', required: true },
                        { key: 'email', label: t('email'), type: 'email', required: true },
                        { key: 'phone', label: t('phone'), type: 'tel', required: false },
                      ].map(f => (
                        <div key={f.key}>
                          <label htmlFor={`field-${f.key}`} className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">
                            {f.label} {f.required && <span className="text-destructive" aria-hidden="true">*</span>}
                          </label>
                          <input id={`field-${f.key}`} type={f.type} required={f.required} value={form[f.key as keyof typeof form]}
                            onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                            className="w-full bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors"
                            maxLength={200} aria-required={f.required} />
                        </div>
                      ))}

                      <div>
                        <label htmlFor="message-input" className="text-xs text-muted-foreground tracking-wider uppercase mb-2 block">{t('message')}</label>
                        <textarea id="message-input" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                          rows={4} maxLength={1000} className="w-full bg-transparent border-b border-border py-3 text-foreground focus:outline-none focus:border-accent focus:ring-1 focus:ring-ring transition-colors resize-none" />
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <button type="button"
                          onClick={() => { setStep(1); const el = document.getElementById('booking-form'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                          className="px-8 py-4 text-sm tracking-[0.15em] uppercase border border-border rounded-sm hover:bg-muted transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                          <ArrowLeft size={16} aria-hidden="true" /> {t('back')}
                        </button>
                        <button type="submit" disabled={submitting || !!pricing?.error}
                          className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors flex-1 sm:flex-none disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                          {submitting ? t('sending') : t('sendEnquiry')}
                        </button>
                      </div>
                    </fieldset>
                  )}
                </form>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <div>
                <p className="text-luxury mb-4">{t('option2')}</p>
                <h2 className="font-serif text-3xl font-light mb-2">{t('bookDirectly')}</h2>
                <div className="w-12 h-px bg-golden mb-8" />
                <p className="text-muted-foreground leading-relaxed mb-8">{t('bookDirectlyDesc')}</p>
                <a href="https://www.booking.com/hotel/gr/sivota-royal.en-gb.html" target="_blank" rel="noopener noreferrer"
                  className="inline-block bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors mb-12 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  {t('checkAvailability')}
                </a>

                <div className="border-t border-border pt-8 mt-4">
                  <p className="text-luxury mb-4">WhatsApp</p>
                  <h3 className="font-serif text-2xl font-light mb-4">{t('chatWithUs')}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{t('chatWithUsDesc')}</p>
                  <a href={`https://wa.me/306948561312?text=${encodeURIComponent(t('whatsappMessage'))}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-3.5 text-sm tracking-[0.1em] uppercase hover:opacity-90 transition-opacity rounded-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                    {t('chatOnWhatsApp')}
                  </a>
                </div>

                <div className="mt-12 image-zoom aspect-[4/3] rounded-sm overflow-hidden">
                  <img src={originalZeusPool} alt="Zeus pool area" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
